'use client';

import { useState, useEffect } from 'react';
import { useRouter, useParams } from 'next/navigation';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/toast';
import { partsApi, type Part } from '@parts/api';
import { partGroupsApi, type PartGroup } from '@/services/api/part-groups';
import { useTranslation } from '@/hooks/useTranslation';
import { DynamicForm } from '../../create/components/DynamicForm';
import { GroupMultiSelect } from '../../create/components/GroupMultiSelect';

export default function EditPartPage() {
  const router = useRouter();
  const params = useParams();
  const partId = params.id as string;
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [groups, setGroups] = useState<PartGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  
  const [formData, setFormData] = useState({
    partNumber: '',
    partNameEn: '',
    partNameCn: '',
    unit: 'pcs',
    source: '',
    specifications: '',
    remark: '',
    station: '',
    warehouseLocation: '',
    currentStock: 0,
    minStock: 0,
    maxStock: null as number | null,
    customFields: {} as Record<string, any>,
  });
  
  // 零件编号验证状态
  const [partNumberValidation, setPartNumberValidation] = useState<{
    checking: boolean;
    exists: boolean;
    message: string;
  }>({
    checking: false,
    exists: false,
    message: '',
  });
  
  // 原始零件编号（用于判断是否修改）
  const [originalPartNumber, setOriginalPartNumber] = useState('');
  
  useEffect(() => {
    loadGroups();
    loadPart();
  }, [partId]);
  
  const loadGroups = async () => {
    try {
      const response = await partGroupsApi.getGroups({ page: 1, limit: 100 });
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to load groups:', error);
      toast.error(t.parts.loadGroupsFailed);
    }
  };
  
  const loadPart = async () => {
    try {
      setLoading(true);
      const response = await partsApi.get(partId);
      const part = (response as any).data || response; // 兼容两种返回格式
      
      console.log('Loaded part:', part); // 调试日志
      
      // 设置表单数据
      setFormData({
        partNumber: part.partNumber || '',
        partNameEn: part.partNameEn || '',
        partNameCn: part.partNameCn || '',
        unit: part.unit || 'pcs',
        source: (part as any).source || '',  // source 不在类型定义中，但可能存在
        specifications: part.specifications || '',
        remark: (part as any).remark || '',  // remark 不在类型定义中，但可能存在
        station: part.station || '',
        warehouseLocation: part.warehouseLocation || '',
        currentStock: part.currentStock || 0,
        minStock: part.minStock || 0,
        maxStock: part.maxStock || null,
        customFields: part.customFields || {},
      });
      
      // 保存原始零件编号
      setOriginalPartNumber(part.partNumber || '');
      
      // 设置分组 - groups 结构是 Array<{ group: PartGroup, ... }>
      if (part.groups && part.groups.length > 0) {
        setSelectedGroupIds([part.groups[0].group.id]);
      }
    } catch (error: any) {
      console.error('Failed to load part:', error);
      toast.error(t.parts.loadFailed || '加载零件失败');
      router.push('/parts/manage');
    } finally {
      setLoading(false);
    }
  };
  
  // 防抖定时器
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  
  const handleFieldChange = (key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    
    // 实时验证零件编号
    if (key === 'partNumber') {
      // 清除之前的定时器
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      
      if (!value || value.trim() === '') {
        setPartNumberValidation({
          checking: false,
          exists: false,
          message: '',
        });
        return;
      }
      
      // 如果编号没有改变，不需要验证
      if (value.trim() === originalPartNumber) {
        setPartNumberValidation({
          checking: false,
          exists: false,
          message: '',
        });
        return;
      }
      
      // 设置检查状态
      setPartNumberValidation({
        checking: true,
        exists: false,
        message: '',
      });
      
      // 防抖：500ms 后执行验证
      const timer = setTimeout(() => {
        validatePartNumber(value.trim());
      }, 500);
      
      setDebounceTimer(timer);
    }
  };

  const validatePartNumber = async (partNumber: string) => {
    try {
      // 检查零件编号是否已存在
      const response = await partsApi.list({ partNumber, limit: 10 });
      const exists = response.items.some((p: any) => p.partNumber === partNumber);
      
      const message = exists 
        ? (t.parts.partNumberExists || '零件编号 "{partNumber}" 已存在').replace('{partNumber}', partNumber)
        : '';
      
      setPartNumberValidation({
        checking: false,
        exists: exists,
        message: message,
      });
      
      if (exists) {
        toast.warning(message);
      }
    } catch (error) {
      console.error('Validation error:', error);
      setPartNumberValidation({
        checking: false,
        exists: false,
        message: '',
      });
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      // 过滤掉 "none"，只保留真实的分组 ID
      const realGroupIds = selectedGroupIds.filter(id => id !== 'none');
      
      // 提交零件数据
      const partData: any = {
        ...formData,
        groupIds: realGroupIds.length > 0 ? realGroupIds : undefined,
      };
      
      // 移除值为 null、undefined 或空字符串的字段
      Object.keys(partData).forEach(key => {
        const value = partData[key];
        if (value === null || value === undefined || value === '') {
          delete partData[key];
        }
      });
      
      await partsApi.update(partId, partData);
      toast.success(t.parts.updateSuccess || '更新成功');
      
      // 延迟跳转
      setTimeout(() => {
        router.push('/parts/manage');
      }, 500);
    } catch (error: any) {
      console.error('Failed to update part:', error);
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           t.parts.updateFailed || '更新失败';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部操作栏 */}
      <div className="bg-white border-b">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between">
            {/* 左侧：返回 + 分组选择 */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.push('/parts/manage')}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              
              <GroupMultiSelect
                groups={groups}
                selectedGroupIds={selectedGroupIds}
                onSelectionChange={setSelectedGroupIds}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="px-8 py-8">
        <div className="max-w-5xl mx-auto">
          <form 
            onSubmit={handleSubmit} 
            className="space-y-6">
            <DynamicForm
              groups={groups}
              selectedGroupIds={selectedGroupIds}
              formData={formData}
              onFieldChange={handleFieldChange}
              partNumberValidation={partNumberValidation}
            />
            
            {/* 提交按钮 */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => router.push('/parts/manage')}
              >
                {t.common.cancel}
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                onClick={(e) => {
                  // 先验证分组
                  if (selectedGroupIds.length === 0) {
                    e.preventDefault();
                    toast.error(t.parts.pleaseSelectGroup);
                    const groupSelector = document.querySelector('[data-group-selector]');
                    if (groupSelector) {
                      groupSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t.parts.updating || '更新中...'}
                  </>
                ) : (
                  t.parts.updateButton || '更新零件'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

